import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpContext } from "@angular/common/http";
import { OAuthService } from "angular-oauth2-oidc";
import { APPLICATION_ID_CONTEXT, IS_LOGGING_ENABLED, LOG_ONLY_ON_ERROR, PROCESS_ID_CONTEXT } from "../interceptors/altinn-request.interceptor";
import { bearerTokenHeaders } from "../authorization-headers";

export interface IAltinn3SubmissionResponse {
  success: boolean;
  errorMessage: string;
  instanceId: string;
  altinnSubmissionId: string;
  attachmentsToUpload: string[];
}

@Injectable({
  providedIn: 'root'
})
export class BackendAltinn3SubmissionService {

  private readonly endpointApplicationPath = "api/application";

  constructor(
    protected readonly httpClient: HttpClient,
    private readonly oauthService: OAuthService,
  ) {
  }

  prepareSubmission(applicationId: string, processId: string, birthNo: string | null): Observable<IAltinn3SubmissionResponse> {
    const endpoint = `${environment.apiUrl}/${this.endpointApplicationPath}/${applicationId}/altinn3/prepare`;
    return this.httpClient.post<IAltinn3SubmissionResponse>(endpoint, { birthNo }, {
      headers: bearerTokenHeaders(this.oauthService),
      context: this.loggingContext(applicationId, processId, false),
    });
  }

  // Returns once the backend has accepted the kick-off (HTTP 202). Actual upload completion
  // is reported asynchronously over the AltinnSubmissionProgressHub SignalR connection.
  uploadAttachment(
    applicationId: string,
    processId: string,
    submissionId: string,
    attachmentId: string,
    altinnInstanceId: string,
  ): Observable<void> {
    const endpoint = `${environment.apiUrl}/${this.endpointApplicationPath}/${applicationId}/altinn3/upload-attachment`;
    return this.httpClient.post<void>(endpoint, { submissionId, attachmentId, altinnInstanceId }, {
      headers: bearerTokenHeaders(this.oauthService),
      context: this.loggingContext(applicationId, processId, true),
    });
  }

  // Returns once the backend has accepted the kick-off (HTTP 202). The finalize outcome is
  // pushed to the frontend via the SignalR hub (joined by altinnSubmissionId).
  startFinalize(
    applicationId: string,
    processId: string,
    submissionId: string,
    altinnInstanceId: string,
  ): Observable<void> {
    const endpoint = `${environment.apiUrl}/${this.endpointApplicationPath}/${applicationId}/altinn3/finalize`;
    return this.httpClient.post<void>(endpoint, { submissionId, altinnInstanceId }, {
      headers: bearerTokenHeaders(this.oauthService),
      context: this.loggingContext(applicationId, processId, false),
    });
  }

  // Prepare and finalize are archived as submission milestones; attachment kick-offs are
  // archived only when they fail, to keep one submission down to a handful of entries.
  private loggingContext(applicationId: string, processId: string, onlyOnError: boolean) {
    return new HttpContext()
      .set(IS_LOGGING_ENABLED, true)
      .set(APPLICATION_ID_CONTEXT, applicationId)
      .set(PROCESS_ID_CONTEXT, processId)
      .set(LOG_ONLY_ON_ERROR, onlyOnError);
  }

}
